import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Minus, Plus, ShoppingBag, ArrowLeft, Check } from 'lucide-react';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProductPage = () => {
  const { id } = useParams();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const res = await axios.get(`${API}/products/${id}`);
        setProduct(res.data);
      } catch (err) {
        console.error('Error fetching product:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id]);

  const handleAddToCart = () => {
    if (product) {
      addItem(product, quantity);
      setAdded(true);
      toast.success(`${product.name} added to cart`);
      setTimeout(() => setAdded(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-28 pb-24 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 animate-pulse">
            <div className="aspect-[3/4] bg-secondary/30" />
            <div className="space-y-6">
              <div className="h-8 bg-secondary/30 w-3/4" />
              <div className="h-6 bg-secondary/30 w-1/4" />
              <div className="h-32 bg-secondary/30" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen pt-28 pb-24 px-6 md:px-12 flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-heading text-2xl mb-4">Product not found</h2>
          <Link to="/shop" className="text-gold hover:text-gold-light">
            Return to shop
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-28 pb-24 px-6 md:px-12">
      <div className="max-w-7xl mx-auto">
        {/* Back Link */}
        <Link
          to="/shop"
          className="inline-flex items-center text-muted-foreground hover:text-gold transition-colors mb-8 text-sm"
          data-testid="back-to-shop"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Shop
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Product Image */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="aspect-[3/4] overflow-hidden bg-secondary/30"
          >
            <img
              src={product.image_url}
              alt={product.name}
              className="w-full h-full object-cover"
              data-testid="product-detail-image"
            />
          </motion.div>

          {/* Product Details */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex flex-col"
          >
            {/* Category */}
            <span className="text-xs font-accent tracking-widest uppercase text-gold mb-4">
              {product.category}
            </span>

            {/* Name */}
            <h1 className="font-heading text-3xl md:text-4xl mb-4" data-testid="product-detail-name">
              {product.name}
            </h1>

            {/* Price */}
            <p className="font-accent text-2xl text-[#D4AF37] mb-6" data-testid="product-detail-price">
              ${product.price.toFixed(2)}
            </p>

            {/* Description */}
            <p className="text-muted-foreground leading-relaxed mb-8">
              {product.description}
            </p>

            {/* Stock Status */}
            <div className="flex items-center gap-2 mb-8">
              {product.stock > 0 ? (
                <>
                  <span className="w-2 h-2 bg-green-500 rounded-full" />
                  <span className="text-sm text-muted-foreground">
                    In Stock ({product.stock} available)
                  </span>
                </>
              ) : (
                <>
                  <span className="w-2 h-2 bg-red-500 rounded-full" />
                  <span className="text-sm text-muted-foreground">Out of Stock</span>
                </>
              )}
            </div>

            {/* Quantity */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-sm text-muted-foreground uppercase tracking-widest">
                Quantity
              </span>
              <div className="flex items-center border border-white/20">
                <button
                  onClick={() => setQuantity(q => Math.max(1, q - 1))}
                  className="p-3 hover:bg-white/5 transition-colors"
                  data-testid="qty-decrease"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <span className="w-12 text-center" data-testid="qty-value">
                  {quantity}
                </span>
                <button
                  onClick={() => setQuantity(q => Math.min(product.stock, q + 1))}
                  className="p-3 hover:bg-white/5 transition-colors"
                  data-testid="qty-increase"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Add to Cart */}
            <Button
              onClick={handleAddToCart}
              disabled={product.stock === 0}
              className={`w-full py-6 text-xs tracking-widest uppercase btn-shine font-medium ${
                added
                  ? 'bg-green-600 hover:bg-green-600'
                  : 'bg-[#D4AF37] text-black hover:bg-[#B5952F]'
              }`}
              data-testid="add-to-cart-btn"
            >
              {added ? (
                <>
                  <Check className="mr-2 h-4 w-4" />
                  Added to Cart
                </>
              ) : (
                <>
                  <ShoppingBag className="mr-2 h-4 w-4" />
                  Add to Bag - ${(product.price * quantity).toFixed(2)}
                </>
              )}
            </Button>

            {/* Features */}
            <div className="mt-12 pt-8 border-t border-white/10">
              <div className="grid grid-cols-2 gap-4">
                {[
                  'Free shipping over $50',
                  '30-day returns',
                  'Secure checkout',
                  'Premium quality',
                ].map(feature => (
                  <div key={feature} className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Check className="h-4 w-4 text-gold" />
                    {feature}
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ProductPage;
